/**
 * API Client
 * Main API client class wrapping Axios with singleton pattern
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, API_CONFIG } from './config';
import { setupInterceptors } from './interceptors';
import type { UseBoundStore, StoreApi } from 'zustand';
import type { AppStore } from '../state/types';
import type { ApiResponse } from './types';

class ApiClient {
  private instance: AxiosInstance | null = null;
  private store: UseBoundStore<StoreApi<AppStore>> | null = null;

  /**
   * Initialize API client with store
   */
  initialize(store: UseBoundStore<StoreApi<AppStore>>): AxiosInstance {
    if (this.instance) {
      return this.instance;
    }

    this.store = store;

    // Create axios instance
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_CONFIG.timeout,
      headers: API_CONFIG.headers,
    });

    // Setup interceptors
    setupInterceptors(this.instance, store);

    return this.instance;
  }

  /**
   * Get API client instance
   */
  getInstance(): AxiosInstance {
    if (!this.instance) {
      throw new Error('API client not initialized. Call createApiClient(store) first.');
    }
    return this.instance;
  }

  /**
   * GET request
   */
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.getInstance().get(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.getInstance().post(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.getInstance().put(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.getInstance().patch(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.getInstance().delete(url, config);
    return response.data;
  }
}

// Singleton instance
const apiClient = new ApiClient();

/**
 * Create and initialize API client
 */
export const createApiClient = (store: UseBoundStore<StoreApi<AppStore>>): AxiosInstance => {
  return apiClient.initialize(store);
};

/**
 * Get API client instance
 */
export const getApiClient = (): ApiClient => {
  return apiClient;
};

/**
 * Legacy API function for backward compatibility
 */
export const api = async <T = any>(options: {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: any;
  params?: any;
}): Promise<ApiResponse<T>> => {
  const { endpoint, method = 'GET', data, params } = options;
  const client = getApiClient();

  switch (method) {
    case 'GET':
      return client.get<T>(endpoint, { params });
    case 'POST':
      return client.post<T>(endpoint, data);
    case 'PUT':
      return client.put<T>(endpoint, data);
    case 'PATCH':
      return client.patch<T>(endpoint, data);
    case 'DELETE':
      return client.delete<T>(endpoint);
    default:
      throw new Error(`Unsupported method: ${method}`);
  }
};

